<template>
  <TableComp
    v-if="emailBoxes"
    :name="$t('global.email-boxes')"
    :headers="headers"
    :status="emailBoxes.status"
    :pagination="emailBoxes.pagination"
    :sort="emailBoxes.sort"
    :number-of-items="emailBoxes.list.length"
    empty-image="email-boxes"
    :empty-bold-message="$t('global.no-email-boxes-bold-message')"
    :empty-message="$t('global.no-email-boxes-message')"
    v-on:update:list="updateList"
    :header-actions="tableActions"
    :empty-actions="getEmptyActions()"
  >
    <template #desktop>
      <tr v-for="emailBox in emailBoxes.list" v-bind:key="emailBox.id">
        <td>
          <span class="mr-2">{{ emailBox.email }}</span>
          <CopyToClipboard :text="emailBox.email" />
        </td>

        <td class="text-center">
          <span v-if="!emailBox.totalBytes">{{ $t("global.unlimited") }}</span>
          <span v-else>{{ emailBox.usedBytes }} / {{ emailBox.totalBytes }}</span>
        </td>

        <td class="text-center">
          <span class="mr-2">{{ site.cloudHostDomain }}</span>
          <CopyToClipboard :text="site.cloudHostDomain" />
        </td>

        <td class="text-end actions">
          <div class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(emailBox)" />
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow v-for="emailBox in emailBoxes.list" v-bind:key="emailBox.id">
        <template #header>
          <div class="d-flex align-items-center">
            <!-- email box name column -->
            <div class="flex-fill">
              <div class="d-flex align-items-center">
                <span class="mr-2">{{ emailBox.email }}</span>
                <CopyToClipboard :text="emailBox.email" />
              </div>
            </div>

            <!-- dropdown menu column -->
            <div>
              <div class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(emailBox)" />
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <!-- email box name -->
          <div class="row">
            <div class="col">{{ $t("global.name") }}:</div>
            <div class="col">
              <span class="mr-2">{{ emailBox.email }}</span>
              <CopyToClipboard :text="emailBox.email" />
            </div>
          </div>

          <!-- email box storage -->
          <div class="row">
            <div class="col">{{ $t("global.storage") }}:</div>
            <div class="col" v-if="!emailBox.totalBytes">{{ $t("global.unlimited") }}</div>
            <div class="col" v-else>{{ emailBox.usedBytes }} / {{ emailBox.totalBytes }}</div>
          </div>

          <!-- email box hostname -->
          <div class="row">
            <div class="col">{{ $t("global.hostname") }}:</div>
            <LoadingState v-if="siteStatus.isLoading" />
            <ErrorState v-if="siteStatus.isErrored" />
            <div class="col" v-else>
              <span class="mr-2">{{ site.cloudHostDomain }}</span>
              <CopyToClipboard :text="site.cloudHostDomain" />
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import CopyToClipboard from "@/components/display/CopyToClipboard";
import EmailBoxAddDisabled from "@/components/smart/modals/EmailBoxAddDisabled";
import EmailBoxAddModal from "@/components/smart/modals/EmailBoxAdd";
import EmailBoxDelete from "@/components/smart/modals/EmailBoxDelete";
import EmailPasswordGenerateModal from "@/components/smart/modals/EmailPasswordGenerate";
import ErrorState from "@/components/display/ErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import Status from "@/classes/Status";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";
import ViewPasswordModal from "@/components/smart/modals/ViewPassword";

export default {
  name: "EmailBoxesTable",
  props: {
    emailBoxes: Object,
    localMailDeliveryEnabled: {
      type: Boolean,
      required: true,
    },
    site: {
      type: Object,
    },
    siteStatus: {
      type: Status,
    },
  },
  components: {
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
    CopyToClipboard,
    LoadingState,
    ErrorState,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.name")),
      new TableHeader(root.$t("global.storage"), "justify-content-center"),
      new TableHeader(root.$t("global.hostname"), "justify-content-center"),
      new TableHeader(),
    ];

    const addEmailBox = () => {
      if (props.localMailDeliveryEnabled) {
        store.dispatch("modals/open", {
          template: EmailBoxAddModal,
          data: { site: props.site },
        });
      } else {
        store.dispatch("modals/open", {
          template: EmailBoxAddDisabled,
          data: { site: props.site },
        });
      }
    };

    const tableActions = [
      new NavLink(root.$t("global.add-email"), "add", addEmailBox, false, null, "epsilon"),
    ];

    const getEmptyActions = () => [
      new NavLink(root.$t("global.add-email"), null, addEmailBox, false, "primaryOutline", null),
    ];

    const viewPassword = (emailBox) => {
      store.dispatch("modals/open", {
        template: ViewPasswordModal,
        data: { password: emailBox.password },
      });
    };

    const regeneratePassword = (emailBox) => {
      store.dispatch("modals/open", {
        template: EmailPasswordGenerateModal,
        data: { emailBox, site: props.site },
      });
    };

    const deleteEmailBox = (emailBox) => {
      store.dispatch("modals/open", {
        template: EmailBoxDelete,
        data: { emailBox, site: props.site },
      });
    };

    const menuItems = (emailBox) => {
      let items = [];

      // only allow `View Password` if a password exists for this email box
      if (emailBox.password) {
        items.unshift(
          new NavLink(root.$t("global.view-password"), null, () => viewPassword(emailBox))
        );
      }

      items = [
        ...items,
        // regenerate password for email box
        new NavLink(root.$t("global.regenerate-password"), null, () =>
          regeneratePassword(emailBox)
        ),
        // delete email box
        new NavLink(root.$t("global.delete"), null, () => deleteEmailBox(emailBox), null, "danger"),
      ];

      return items;
    };

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("emailBoxes/getList", {
        siteId: props.site.id,
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    return {
      getEmptyActions,
      headers,
      menuItems,
      updateList,
      tableActions,
    };
  },
};
</script>
